<template>
  <RfDialog
    ref="dialog"
    title="Segments"
    width="720"
    confirmText="Save"
    cancelText="Close"
    keepOpenOnConfirm
    :disabled="confirmDisabledMessage"
    @confirm="handleConfirm"
    @open="syncSelection"
  >
    <RfSegmentSelect v-model="linkedSegments" :isPush="isScheduledPush" requiredTitle />
  </RfDialog>
</template>

<script setup>
import { computed, ref } from "vue";
import { PromptTypes } from "@/utils/constants/PromoTypesConstants";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfSegmentSelect from "@/blocks/RfSegmentSelect.vue";
import { cloneDeep } from "lodash-es";
import { useAllSegmentsStore } from "@/pinia/allSegmentsStore";

defineOptions({ name: "RfLinkedSegments" });

const props = defineProps({
  model: { type: Object, required: true },
});
const emit = defineEmits(["confirm"]);

const allSegmentsStore = useAllSegmentsStore();

const dialog = ref();
const linkedSegments = ref([...(props.model?.segments || [])]);

const isScheduledPush = computed(() => props.model?.path_type === PromptTypes.scheduled_push.value);

// String disables the confirm button and renders as its tooltip (RfButtonNew).
const confirmDisabledMessage = computed(() => {
  if (!props.model?.id) return false;
  if (!linkedSegments.value.length) return "Select at least one segment";
  const hasMissing = linkedSegments.value.some(
    segment => !(allSegmentsStore.segments || []).some(seg => seg.id === (segment?.id ?? segment)),
  );
  return hasMissing ? "Selected segments are no longer available" : false;
});

// The selection is a snapshot taken when the dialog opens — reopening discards
// unsaved edits and picks up an externally updated model.
const syncSelection = () => {
  linkedSegments.value = [...(props.model?.segments || [])];
};

const open = () => dialog.value?.open();
const close = () => dialog.value?.close();

const handleConfirm = () => {
  const next = cloneDeep(props.model);
  try {
    fillModel(next);
  } catch {
    // No valid segments selected — keepOpenOnConfirm leaves the dialog open.
    return;
  }
  emit("confirm", next);
  close();
};

const fillModel = model => {
  const linked = [];
  linkedSegments.value.forEach(segment => {
    const id = segment?.id || segment;
    const matched = (allSegmentsStore.segments || []).find(s => s.id === id);
    if (matched) {
      linked.push({ id: matched.id, name: matched.name });
    }
  });
  model.segments = linked;
  if (model.id && !model.segments.length) {
    throw new Error("At least one segment required");
  }
};

defineExpose({ open, close });
</script>
